import { api } from '@/shared/api/client'
import { qs } from '@/shared/lib/query'
import type {
  ApiKeyReportResponse, ApiKeyResponse, CreateApiKeyResponse, CredentialResponse,
  CursorPage, DeliveryResponse, HealthCheckResponse, InboundEventResponse,
  IntegrationCallResponse, IntegrationReportResponse, IntegrationResponse,
  LocalDecryptResponse, ProviderResponse, RotateSecretResponse, UsageResponse,
  WebhookEndpointResponse, WebhookEventTypeResponse, WebhookReportResponse,
} from '@/shared/api/types'

/**
 * `integration` endpoints, read from the shipped controllers.
 *
 * **There is no `credentialValue()` function in this module and there must never
 * be one.** The backend exposes no endpoint that returns credential plaintext —
 * not for an admin, not for a holder of `*:*` — so there is nothing for such a
 * function to call. `CredentialResponse` carries a fingerprint and that is the
 * whole of what any human ever sees again after the moment they paste a secret in.
 *
 * The single exception in the entire API is `createApiKey`, whose `201` body
 * carries the generated key exactly once. `CreateApiKeyResponse` is the only
 * response type in the app with secret material in it, and the UI that renders it
 * has to say plainly that there is no second chance.
 */

export const listIntegrations = () =>
  api.get<IntegrationResponse[]>('/integrations')

export const listProviders = () =>
  api.get<ProviderResponse[]>('/integrations/providers')

export const getIntegration = (id: string) =>
  api.get<IntegrationResponse>(`/integrations/${id}`)

export const createIntegration = (body: unknown) =>
  api.post<IntegrationResponse>('/integrations', body)

/** PATCH requires `If-Match` carrying the row version; a missing header is a 400. */
export const updateIntegration = (id: string, body: unknown, version: number) =>
  api.patch<IntegrationResponse>(`/integrations/${id}`, body, {
    headers: { 'If-Match': String(version) },
  })

export const enableIntegration = (id: string) =>
  api.post<IntegrationResponse>(`/integrations/${id}/enable`)

export const disableIntegration = (id: string) =>
  api.post<IntegrationResponse>(`/integrations/${id}/disable`)

export const runHealthCheck = (id: string) =>
  api.post<HealthCheckResponse>(`/integrations/${id}/health-check`)

export const integrationUsage = (id: string) =>
  api.get<UsageResponse>(`/integrations/${id}/usage`)

// ── credentials ─────────────────────────────────────────────────────────────

/** Fingerprints, status and expiry. Never a value — see the module note. */
export const listCredentials = (id: string) =>
  api.get<CredentialResponse[]>(`/integrations/${id}/credentials`)

/**
 * Store or rotate. The plaintext travels once, in this request body, and is
 * never returned. Posting a kind that already exists creates the next version in
 * `ROTATING` — both work until one is promoted.
 */
export const putCredential = (id: string, body: unknown) =>
  api.post<CredentialResponse>(`/integrations/${id}/credentials`, body)

export const promoteCredential = (id: string, cid: string) =>
  api.post<CredentialResponse>(`/integrations/${id}/credentials/${cid}/promote`)

export const revokeCredential = (id: string, cid: string, reason: string) =>
  api.post<CredentialResponse>(`/integrations/${id}/credentials/${cid}/revoke`, { reason })

/**
 * Decrypts every credential on the integration locally. It does **not** contact
 * the provider — the response is deliberately named `decryptable` so it cannot
 * be mistaken for a reachability check.
 */
export const testCredentialsLocally = (id: string) =>
  api.post<LocalDecryptResponse>(`/integrations/${id}/credentials/test`)

/**
 * Calls the provider with one specific credential version, without promoting it.
 *
 * ⚠️ For an OAuth2 provider this currently returns a false green: the token
 * cache is keyed on integration id alone, so the call is made with the *active*
 * credential rather than the one under test (prompt 25 §5). The UI says so.
 */
export const testCredential = (id: string, cid: string) =>
  api.post<HealthCheckResponse>(`/integrations/${id}/credentials/${cid}/test`)

// ── reporting (prompt 24 §3) ────────────────────────────────────────────────

export interface ReportRange extends Record<string, string | undefined> {
  from?: string
  to?: string
  bucket?: 'DAY' | 'HOUR'
  capability?: string
}

export const integrationReport = (id: string, r: ReportRange = {}) =>
  api.get<IntegrationReportResponse>(`/integrations/${id}/report${qs(r)}`)

export const integrationCalls = (
  id: string,
  p: { from?: string; to?: string; capability?: string; outcome?: string; cursor?: string; size?: number } = {},
) => api.get<CursorPage<IntegrationCallResponse>>(`/integrations/${id}/calls${qs(p)}`)

// ── inbound events, webhooks, api keys ──────────────────────────────────────

/**
 * `InboundEventController.list` binds no query parameters, so this takes none.
 * An earlier signature accepted `integrationId` and `status` and silently
 * returned an unfiltered list — a trap for the next caller.
 */
export const listInboundEvents = () =>
  api.get<InboundEventResponse[]>('/integrations/inbound-events')

export const replayInboundEvent = (id: string) =>
  api.post<void>(`/integrations/inbound-events/${id}/replay`)

export const listWebhooks = () =>
  api.get<WebhookEndpointResponse[]>('/webhooks')

/**
 * The publishable event catalogue that populates the subscribed-events picker.
 *
 * ⚠️ Returns `[]` on the current backend — the catalogue scanner was built
 * without an include filter, so it matches nothing (prompt 25 §3). The picker
 * falls back to free entry and says why, rather than showing an empty list that
 * looks like "there are no events".
 */
export const listWebhookEventTypes = () =>
  api.get<WebhookEventTypeResponse[]>('/webhooks/event-types')

export const createWebhook = (body: unknown) =>
  api.post<WebhookEndpointResponse>('/webhooks', body)

export const updateWebhook = (id: string, body: unknown) =>
  api.patch<WebhookEndpointResponse>(`/webhooks/${id}`, body)

export const pauseWebhook = (id: string) =>
  api.post<WebhookEndpointResponse>(`/webhooks/${id}/pause`)

export const resumeWebhook = (id: string) =>
  api.post<WebhookEndpointResponse>(`/webhooks/${id}/resume`)

export const deleteWebhook = (id: string) =>
  api.del<void>(`/webhooks/${id}`)

/** The new secret is in the response body and nowhere else, ever. */
export const rotateWebhookSecret = (id: string) =>
  api.post<RotateSecretResponse>(`/webhooks/${id}/rotate-secret`)

export const listDeliveries = (
  id: string,
  p: { status?: string; eventType?: string; cursor?: string; size?: number } = {},
) => api.get<CursorPage<DeliveryResponse>>(`/webhooks/${id}/deliveries${qs(p)}`)

export const webhookReport = (id: string, r: ReportRange = {}) =>
  api.get<WebhookReportResponse>(`/webhooks/${id}/report${qs(r)}`)

/** 202 with an empty body — there is nothing to read back. */
export const retryDelivery = (id: string, did: string) =>
  api.post<void>(`/webhooks/${id}/deliveries/${did}/retry`)

export const listApiKeys = () =>
  api.get<ApiKeyResponse[]>('/api-keys')

/** The `201` body is the only time the key exists in a response. */
export const createApiKey = (body: unknown) =>
  api.post<CreateApiKeyResponse>('/api-keys', body)

export const getApiKey = (id: string) =>
  api.get<ApiKeyResponse>(`/api-keys/${id}`)

export const apiKeyReport = (id: string, r: ReportRange = {}) =>
  api.get<ApiKeyReportResponse>(`/api-keys/${id}/report${qs(r)}`)

export const revokeApiKey = (id: string, reason: string) =>
  api.post<ApiKeyResponse>(`/api-keys/${id}/revoke`, { reason })

// ── labels ──────────────────────────────────────────────────────────────────

export const CATEGORY_LABEL: Record<string, string> = {
  SOCIAL: 'Social', NEWS: 'News', LEGAL: 'Legal', REGISTRY: 'Registry',
  PAYMENT: 'Payment', MESSAGING: 'Messaging', IDENTITY: 'Identity',
  MAPPING: 'Mapping', ANALYTICS: 'Analytics', AI: 'AI', STORAGE: 'Storage',
  OTHER: 'Other',
}

export const AUTH_KIND_LABEL: Record<string, string> = {
  NONE: 'None', API_KEY: 'API key', BEARER: 'Bearer token', BASIC: 'Basic',
  HMAC: 'HMAC', OAUTH2_CLIENT_CREDENTIALS: 'OAuth2 client credentials',
  OAUTH2_AUTH_CODE: 'OAuth2 authorisation code', MTLS: 'Mutual TLS',
}

export const CREDENTIAL_KIND_LABEL: Record<string, string> = {
  API_KEY: 'API key', API_SECRET: 'API secret', BEARER_TOKEN: 'Bearer token',
  ACCESS_TOKEN: 'Access token', REFRESH_TOKEN: 'Refresh token',
  CLIENT_ID: 'Client ID', CLIENT_SECRET: 'Client secret',
  CONSUMER_KEY: 'Consumer key', CONSUMER_SECRET: 'Consumer secret',
  WEBHOOK_SECRET: 'Webhook secret', PASSPHRASE: 'Passphrase',
  PRIVATE_KEY: 'Private key', CERTIFICATE: 'Certificate', SHORTCODE: 'Shortcode',
  PASSKEY: 'Passkey', INITIATOR_PASSWORD: 'Initiator password', USERNAME: 'Username',
}
